import { IShipment, Shipment } from "./Shipment";
import { getServerDate } from "../utils/storage/date";
import { getShipmentsFromState } from "../ui/assets/helper";
import { DriveStore } from "../store/reducers";
import { extend, hashToArray } from "../utils/vanilla";
import { UserService } from "../utils/users";
import { getCurrentVehicle } from "../utils/device";
import { CancelablePromise } from "../utils/cancelablePromise";
import { IReportPeriod } from "../utils/date";
import { TMultiCallParam, IApiError } from "../utils/api.interfaces";
import Api from "../services/api";
import * as logger from "../utils/logger";

export function convertStorageShipmentToDictionary(logs: DeepReadonlyObject<IIdDictionary<IShipment>>): IIdDictionary<IShipment> {
    let shipmentsDictionary: IIdDictionary<IShipment> = {};
    Object.keys(logs).forEach(key => shipmentsDictionary[key] = extend(true, new Shipment(), logs[key]));
    return shipmentsDictionary;
}

export function getShipmentsByUser(store: DriveStore, userId: string | undefined): IIdDictionary<IShipment> {
    const shipments = userId && store.getState().shipments || {};
    const userShipments: IIdDictionary<IShipment> = {};
    Object.keys(shipments).forEach(key => {
        const deviceShipments = shipments[key];
        Object.keys(deviceShipments).forEach(shipmentId => {
            const shipment = deviceShipments[shipmentId];
            if (typeof (shipment.driver) === "object" && shipment.driver.id === userId) {
                userShipments[shipment.id] = shipment;
            }
        });
    });
    return convertStorageShipmentToDictionary(userShipments);
}

export function getShipmentsByDevice(store: DriveStore, deviceId: string | undefined): IIdDictionary<IShipment> {
    const shipments = deviceId && store.getState().shipments[deviceId] || {};
    return convertStorageShipmentToDictionary(shipments);
}

export function getShipments(store: DriveStore): IIdDictionary<IShipment> {
    return getShipmentsByDevice(store, getCurrentVehicle(store)?.id);
}

export function getActiveShipments(userService: UserService, store: DriveStore, checkTempAssets = true): IShipment[] {
    const now = getServerDate().getTime();
    const shipments = getShipments(store);
    let shipmentArray = hashToArray(shipments);
    let totalActiveShipments: IShipment[] = [];

    if (checkTempAssets) {
        let tempShipments = getShipmentsFromState(store, userService);
        shipmentArray = shipmentArray.concat(tempShipments);
    }

    //Filter out any shipments not in date range
    shipmentArray.forEach(shipment => {
        if ((!shipment.activeFrom || now >= new Date(shipment.activeFrom).getTime()) && (!shipment.activeTo || now <= new Date(shipment.activeTo).getTime())) {
            totalActiveShipments.push(shipment);
        }
    });

    totalActiveShipments.sort((a, b) => (a.dateTime || "") < (b.dateTime || "") ? 1 : -1);
    return totalActiveShipments;
}

/**
 * Returns shipment names (documentNumber or commodity) for a given vehicle
 * that are active at the specified reportDate.
 *
 * Reads from the Redux store first. If the store has no data for the device,
 * falls back to the optional fetchedShipments dictionary (API-fetched data).
 */
export function getShipmentNamesForVehicle(
    store: DriveStore,
    reportDate: Date,
    vehicleId: string,
    fetchedShipments?: IIdDictionary<IShipment[]>
): string[] {
    let reportDateTime = reportDate.getTime();

    let vehicleShipments: IIdDictionary<IShipment>;
    const storeShipments = getShipmentsByDevice(store, vehicleId);
    if (Object.keys(storeShipments).length > 0) {
        vehicleShipments = storeShipments;
    } else if (fetchedShipments?.[vehicleId]) {
        const dict: IIdDictionary<IShipment> = {};
        fetchedShipments[vehicleId].forEach(s => { dict[s.id] = s; });
        vehicleShipments = dict;
    } else {
        vehicleShipments = {};
    }

    let shipments = Object.keys(vehicleShipments).reduce((allShipments: IShipment[], shipmentId) => {
        let driverShipment = vehicleShipments[shipmentId];
        let shipmentActiveFrom = driverShipment.activeFrom && new Date(driverShipment.activeFrom).getTime();
        let shipmentActiveTo = driverShipment.activeTo && new Date(driverShipment.activeTo).getTime();
        if (shipmentActiveFrom && shipmentActiveFrom <= reportDateTime && (!shipmentActiveTo || shipmentActiveTo >= reportDateTime)) {
            allShipments.push(driverShipment);
        }
        return allShipments;
    }, []).sort((a, b) => !a.activeTo ? -1 : (!b.activeTo ? -1 : (b.activeTo > a.activeTo ? 1 : -1)));

    return shipments.map(shipment => shipment.documentNumber || shipment.commodity || "");
}

/**
 * Checks which device IDs are missing from the Redux store's shipments and
 * batch-fetches them from the API via multiCall.
 *
 * Returns a dictionary of deviceId -> IShipment[] for the missing devices.
 * On error, logs and returns {} so the report degrades gracefully.
 */
export function fetchMissingShipments(
    api: Api,
    store: DriveStore,
    deviceIds: string[],
    reportPeriod: IReportPeriod
): CancelablePromise<IIdDictionary<IShipment[]>> {
    const storeShipments = store.getState().shipments;
    const missingDeviceIds = deviceIds.filter(
        id => !storeShipments[id] || Object.keys(storeShipments[id]).length === 0
    );

    if (missingDeviceIds.length === 0) {
        return CancelablePromise.resolve({});
    }

    const calls: TMultiCallParam[] = missingDeviceIds.map(deviceId => [
        "Get",
        {
            typeName: "ShipmentLog",
            search: {
                deviceSearch: { id: deviceId },
                fromDate: reportPeriod.fromDate.toISOString(),
                toDate: reportPeriod.toDate.toISOString()
            }
        }
    ]);

    return new CancelablePromise((resolve) => {
        api.multiCall(calls, (results: IShipment[][]) => {
            const fetched: IIdDictionary<IShipment[]> = {};
            missingDeviceIds.forEach((deviceId, index) => {
                fetched[deviceId] = results[index] || [];
            });
            resolve(fetched);
        }, (_: IApiError) => {
            logger.log(
                logger.Source.SYNC,
                "Failed to fetch shipments for non-current devices",
                api.getUser()
            );
            resolve({});
        });
    });
}

export const getUpdatedGroupedShipments = (shipments: IIdDictionary<IShipment>) => {
    const groupedShipments: IIdDictionary<IIdDictionary<IShipment>> = {};
    Object.keys(shipments).forEach(shipmentId => {
        const shipment = shipments[shipmentId];
        const deviceId = typeof (shipment.device) === "object" ? shipment.device.id : shipment.device;
        if (deviceId) {
            if (!groupedShipments[deviceId]) {
                groupedShipments[deviceId] = {};
            }
            groupedShipments[deviceId][shipmentId] = shipment;
        }
    });
    return groupedShipments;
};